import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  isPushSupported,
  isPushDismissed,
  dismissPush,
  subscribePush,
  isCurrentlySubscribed,
  getPushPermission,
  claimPushSubscription,
} from '@/utils/pushNotifications';

export default function PushBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !isPushSupported() || isPushDismissed()) return;

    // Don't show banner if user already accepted marketing push
    if (user.marketing_push_enabled) return;

    (async () => {
      const perm = await getPushPermission();
      if (perm === 'denied') return;
      const subscribed = await isCurrentlySubscribed();
      if (!subscribed) setVisible(true);
    })();
  }, [user]);

  if (!visible) return null;

  const handleActivate = async () => {
    setLoading(true);
    try {
      await subscribePush();
      // After subscribing, claim the subscription to link it to the logged-in user
      await claimPushSubscription();
      setVisible(false);
    } catch (err) {
      if (err.message === 'denied') {
        dismissPush();
      }
      setVisible(false);
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    dismissPush();
    setVisible(false);
  };

  return (
    <div
      data-testid="push-banner"
      className="mx-4 mt-3 flex items-center gap-3 p-3 rounded-xl bg-zinc-900 border border-zinc-800/50 animate-fade-in"
    >
      <div className="w-9 h-9 rounded-lg bg-[#E84C1C]/10 flex items-center justify-center shrink-0">
        <Bell size={18} className="text-[#E84C1C]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">Notificaciones de pedidos</p>
        <p className="text-xs text-zinc-400 truncate">Recibí avisos del estado de tu pedido</p>
      </div>
      <button
        data-testid="push-activate-btn"
        onClick={handleActivate}
        disabled={loading}
        className="shrink-0 px-3 py-1.5 text-xs font-medium bg-[#E84C1C] hover:bg-[#d4411a] rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? '...' : 'Activar'}
      </button>
      <button
        data-testid="push-dismiss-btn"
        onClick={handleDismiss}
        className="shrink-0 text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}
